import { flushSync } from "react-dom";
import { useComputedColorScheme, useMantineColorScheme } from "@mantine/core";

type Scheme = "light" | "dark";

/**
 * Theme switch with a full-screen circular reveal from the top-right corner.
 * Uses the View Transitions API: the browser snapshots the old theme, we
 * apply the new one synchronously, then grow a circle over the new snapshot
 * until it covers the viewport. Falls back to an instant switch where the API
 * is missing or the user prefers reduced motion.
 */
export function useThemeSwitch() {
  const { setColorScheme } = useMantineColorScheme();
  const scheme = useComputedColorScheme("light");

  const switchTo = (next: Scheme) => {
    const apply = () => flushSync(() => setColorScheme(next));
    const doc = document as Document & {
      startViewTransition?: (cb: () => void) => { ready: Promise<void> };
    };
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!doc.startViewTransition || reduced) {
      apply();
      return;
    }
    const x = window.innerWidth;
    const y = 0;
    const radius = Math.hypot(window.innerWidth, window.innerHeight);
    const transition = doc.startViewTransition(apply);
    transition.ready
      .then(() => {
        document.documentElement.animate(
          { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`] },
          { duration: 650, easing: "cubic-bezier(0.65, 0, 0.35, 1)", pseudoElement: "::view-transition-new(root)" },
        );
      })
      .catch(() => {
        // Transition skipped (e.g. tab hidden) — the theme is already applied.
      });
  };

  return {
    scheme,
    isDark: scheme === "dark",
    toggle: () => switchTo(scheme === "dark" ? "light" : "dark"),
    switchTo,
  };
}
